import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Select,
  TextInput,
  Badge,
  DataTable,
} from '@etherisc/ui-kit';
import { ScrollTextIcon, SearchIcon, XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { apiClient } from '../../services/api';

interface AuditLogEntry {
  id: string;
  action: string;
  resource: string;
  resourceId: string | null;
  oldValues: unknown;
  newValues: unknown;
  ipAddress: string | null;
  createdAt: string;
  userId: string | null;
  organizationId: string | null;
  correlationId: string;
  user: { id: string; name: string; email: string } | null;
  organization: { id: string; name: string } | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  LOGIN: 'bg-purple-100 text-purple-800',
  AUTOMATION_RUN: 'bg-orange-100 text-orange-800',
};

export function AuditTrail() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [availableResources, setAvailableResources] = useState<string[]>([]);

  useEffect(() => {
    apiClient.getAuditFilters()
      .then(data => {
        setAvailableActions(data.actions || []);
        setAvailableResources(data.resources || []);
      })
      .catch(() => {});
  }, []);

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '25' };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (resourceFilter) params.resource = resourceFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const data = await apiClient.getAuditLogs(params);
      setLogs(data.logs as AuditLogEntry[]);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [search, actionFilter, resourceFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const clearFilters = () => {
    setSearch('');
    setActionFilter('');
    setResourceFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = search || actionFilter || resourceFilter || dateFrom || dateTo;

  const columns: ColumnDef<AuditLogEntry>[] = [
    {
      id: 'createdAt',
      header: 'Time',
      accessorKey: 'createdAt',
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleString() : '—';
      },
      size: 170,
    },
    {
      id: 'action',
      header: 'Action',
      accessorKey: 'action',
      cell: ({ getValue }) => {
        const action = getValue() as string;
        const colorClass = ACTION_COLORS[action] || 'bg-gray-100 text-gray-800';
        return <Badge className={colorClass}>{action}</Badge>;
      },
      size: 130,
    },
    {
      id: 'resource',
      header: 'Resource',
      accessorKey: 'resource',
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return <span className="font-medium">{val}</span>;
      },
      size: 140,
    },
    {
      id: 'resourceId',
      header: 'Resource ID',
      accessorKey: 'resourceId',
      cell: ({ getValue }) => {
        const val = getValue() as string | null;
        if (!val) return <span className="text-gray-400">—</span>;
        return <span className="font-mono text-xs">{val.substring(0, 8)}...</span>;
      },
      size: 120,
    },
    {
      id: 'user',
      header: 'User',
      accessorFn: (row) => row.user?.name || row.user?.email || row.userId || '—',
      cell: ({ getValue }) => getValue() as string,
      size: 160,
    },
    {
      id: 'organization',
      header: 'Organisation',
      accessorFn: (row) => row.organization?.name || '—',
      cell: ({ getValue }) => getValue() as string,
      size: 150,
    },
    {
      id: 'details',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/audit/${row.original.id}`)}
        >
          View
        </Button>
      ),
      size: 80,
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex items-center gap-3 mb-6">
        <ScrollTextIcon size={28} className="text-gray-700" />
        <h1 className="text-2xl font-bold text-gray-900">Audit Trail</h1>
        <Badge variant="secondary" className="ml-2">{pagination.total} entries</Badge>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Search</label>
              <TextInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ID, resource..."
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Action</label>
              <Select
                value={actionFilter || '__all__'}
                onValueChange={(v) => setActionFilter(v === '__all__' ? '' : v)}
                options={[
                  { value: '__all__', label: 'All actions' },
                  ...availableActions.map(a => ({ value: a, label: a })),
                ]}
                placeholder="All actions"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Resource</label>
              <Select
                value={resourceFilter || '__all__'}
                onValueChange={(v) => setResourceFilter(v === '__all__' ? '' : v)}
                options={[
                  { value: '__all__', label: 'All resources' },
                  ...availableResources.map(r => ({ value: r, label: r })),
                ]}
                placeholder="All resources"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="flex items-center gap-1">
                  <XIcon size={14} /> Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <SearchIcon size={32} className="mx-auto mb-3 text-gray-300" />
          <p>No audit log entries found</p>
          {hasActiveFilters && <p className="text-sm mt-1">Try adjusting your filters</p>}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={logs}
        />
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => fetchLogs(pagination.page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeftIcon size={14} /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchLogs(pagination.page + 1)}
              className="flex items-center gap-1"
            >
              Next <ChevronRightIcon size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
